import { useState } from 'react';
import { Link } from 'react-router';
import { useCurrentUser } from '@/auth/AuthContext';
import { Alert, Button } from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatBRL, formatCompetencia, formatDate, formatDateTime } from '@/lib/format';
import { useApi } from '@/lib/useApi';

interface ClosingRow {
  competencia: string;
  fechado: boolean;
  fechadoEm: string | null;
  fechadoPor: string | null;
  liberaFechamentoEm: string;
  podeFechar: boolean;
  faturamento: string | null;
  metaGlobalAtingida: boolean | null;
  totalPago: string | null;
}

/** Fechamentos: o gestor fecha o mês (definitivo) e exporta; o vendedor vê o próprio resultado. */
export function ClosingsPage() {
  const me = useCurrentUser();
  const gestao = me.role === 'ADMIN' || me.role === 'GESTOR';
  const { data, error, reload } = useApi<ClosingRow[]>('/closings');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);

  async function close(c: string, confirmarPendencias = false) {
    if (!confirmarPendencias && !window.confirm(`Fechar ${formatCompetencia(c)}? Depois de fechado, nada deste mês pode ser alterado.`)) return;
    setBusy(c);
    setMsg(null);
    try {
      await api.post(`/closings/${c}/close`, { confirmarPendencias });
      setMsg({ kind: 'success', text: `${formatCompetencia(c)} fechado. Os valores agora são definitivos.` });
      await reload();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'PENDING_COSTS') {
        setBusy(null);
        if (window.confirm(`${err.message}\n\nFechar mesmo assim?`)) return close(c, true);
        return;
      }
      setMsg({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Fechamentos</h1>
          <p className="muted">
            {gestao
              ? 'O fechamento grava comissões e bônus de cada vendedor. Depois de fechado, o mês não muda mais.'
              : 'Seu resultado nos meses já fechados pelo gestor.'}
          </p>
        </div>
      </header>
      {error && <Alert>{error}</Alert>}
      {msg && <Alert kind={msg.kind}>{msg.text}</Alert>}
      <section className="card flush">
        {!data ? (
          <p className="muted pad">Carregando…</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Mês</th>
                  <th scope="col">Situação</th>
                  {gestao && <th scope="col" className="num">Vendido</th>}
                  {gestao && <th scope="col">Meta da equipe</th>}
                  <th scope="col" className="num">{gestao ? 'Total a pagar' : 'Você recebe'}</th>
                  <th scope="col">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((c) => (
                  <tr key={c.competencia}>
                    <td>
                      <strong>{formatCompetencia(c.competencia)}</strong>
                    </td>
                    <td className="small">
                      {c.fechado ? (
                        <>
                          <span className="badge badge--ok">Fechado</span> {c.fechadoPor} · {formatDateTime(c.fechadoEm)}
                        </>
                      ) : c.podeFechar ? (
                        <span className="badge badge--warn">Pronto para fechar</span>
                      ) : (
                        <span className="muted">Aberto · fechamento libera em {formatDate(c.liberaFechamentoEm)}</span>
                      )}
                    </td>
                    {gestao && <td className="num">{c.faturamento ? formatBRL(c.faturamento) : '—'}</td>}
                    {gestao && <td className="small">{c.metaGlobalAtingida == null ? '—' : c.metaGlobalAtingida ? 'Batida' : 'Não batida'}</td>}
                    <td className="num">{c.totalPago ? <strong>{formatBRL(c.totalPago)}</strong> : '—'}</td>
                    <td className="actions">
                      <Link className="btn btn--secondary" to={`/?mes=${c.competencia}`}>
                        Ver detalhes
                      </Link>
                      {gestao && (
                        <>
                          <a className="btn btn--secondary" href={`/api/closings/${c.competencia}/export.csv?tipo=resumo`}>
                            Planilha resumo
                          </a>
                          <a className="btn btn--secondary" href={`/api/closings/${c.competencia}/export.csv?tipo=vendas`}>
                            Planilha de vendas
                          </a>
                        </>
                      )}
                      {gestao && c.podeFechar && (
                        <Button loading={busy === c.competencia} onClick={() => void close(c.competencia)}>
                          Fechar mês
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
